import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, ArrowRight, Shield, Loader2, AlertCircle, Sparkles, CheckCircle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillingCompleteSetup({ navigate }) {
  const { user, token, logout } = useAuth();
  const [phase, setPhase] = useState('init'); // init | redirecting | slow | error
  const [error, setError] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const mountedRef = useRef(true);
  const attemptedRef = useRef(false);
  const redirectedRef = useRef(false);

  const pendingPlan = user?.pendingPlan;
  const planName = pendingPlan?.planName || 'Starter';
  const billingCycle = pendingPlan?.billingCycle || 'monthly';

  const doRedirect = useCallback((url) => {
    if (!mountedRef.current || redirectedRef.current) return;
    redirectedRef.current = true;
    setPhase('redirecting');
    setCheckoutUrl(url);
    // Redirect immediately — do not delay
    window.location.href = url;
  }, []);

  const createCheckout = useCallback(async () => {
    if (!token || !pendingPlan?.planId) {
      if (mountedRef.current) {
        setPhase('error');
        setError('Missing plan information. Please try again from the pricing page.');
      }
      return;
    }

    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: pendingPlan.planId,
          billing_cycle: billingCycle,
          origin_url: originUrl,
        }),
      });

      if (!mountedRef.current) return;

      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          doRedirect(data.url);
          return;
        }
      }

      // Parse error from non-ok response
      let detail = 'We couldn\'t start secure checkout right now.';
      try {
        const errBody = await res.json();
        if (errBody.detail) detail = errBody.detail;
      } catch {}

      if (mountedRef.current) {
        setPhase('error');
        setError(detail);
      }
    } catch {
      if (mountedRef.current) {
        setPhase('error');
        setError('Connection error. Please check your internet and try again.');
      }
    }
  }, [token, pendingPlan, billingCycle, doRedirect]);

  // Auto-start checkout on mount (once only)
  useEffect(() => {
    mountedRef.current = true;
    if (!attemptedRef.current && pendingPlan && token) {
      attemptedRef.current = true;
      setPhase('init');
      createCheckout();
    } else if (!pendingPlan || !token) {
      setPhase('error');
      setError('Please sign in and select a plan first.');
    }
    return () => { mountedRef.current = false; };
  }, [pendingPlan, token, createCheckout]);

  // Fallback: if still on page after 3s, show manual CTA (fires once from mount)
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (mountedRef.current && !redirectedRef.current) {
        setPhase(prev => prev === 'error' ? prev : 'slow');
      }
    }, 3000);
    return () => clearTimeout(fallbackTimer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManualCheckout = () => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    } else {
      attemptedRef.current = false;
      redirectedRef.current = false;
      setPhase('init');
      setError('');
      createCheckout();
    }
  };

  // ─── All phases render the SAME styled page structure, only content differs ───
  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-lg w-full"
        data-testid="billing-complete-setup"
      >
        {/* ─── INIT / REDIRECTING phase ─── */}
        {(phase === 'init' || phase === 'redirecting') && (
          <div className="text-center" data-testid="billing-setup-loading">
            <Loader2 className="w-10 h-10 text-indigo-400 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-medium text-white mb-3">Setting up your account...</h2>
            <p className="text-zinc-400 leading-relaxed mb-2">
              Your 14-day free trial is almost ready.<br />
              Redirecting you to secure checkout...
            </p>
            <p className="text-sm text-zinc-500 mb-8">
              No charge today. You'll only be billed after your trial ends.
            </p>

            {/* Plan info */}
            {pendingPlan && (
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 mb-6 text-left">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-white font-semibold">{planName} Plan</p>
                    <p className="text-xs text-zinc-500 capitalize">{billingCycle} billing</p>
                  </div>
                  <Sparkles className="w-5 h-5 text-indigo-400" />
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
                  <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" />14-day free trial</span>
                  <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" />No charge today</span>
                  <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" />Cancel anytime</span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-zinc-600 text-xs">
              <Shield className="w-3.5 h-3.5" />
              <span>Secure checkout powered by Stripe</span>
            </div>
          </div>
        )}

        {/* ─── SLOW / timeout phase ─── */}
        {phase === 'slow' && (
          <div className="text-center" data-testid="billing-setup-slow">
            <RefreshCw className="w-10 h-10 text-amber-400 mx-auto mb-6" />
            <h2 className="text-2xl font-medium text-white mb-3">Still working...</h2>
            <p className="text-zinc-400 mb-6">
              If you're not redirected in a few seconds, click below.
            </p>
            <Button
              onClick={handleManualCheckout}
              className="bg-indigo-600 hover:bg-indigo-500 text-white h-12 w-full text-base mb-4"
              data-testid="continue-checkout-btn"
            >
              Continue to Secure Checkout <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <div className="flex items-center justify-center gap-2 text-zinc-600 text-xs">
              <Shield className="w-3.5 h-3.5" />
              <span>Secure checkout powered by Stripe</span>
            </div>
          </div>
        )}

        {/* ─── ERROR phase ─── */}
        {phase === 'error' && (
          <div className="text-center" data-testid="billing-setup-error">
            <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-medium text-white mb-3">
              We couldn't start secure checkout right now.
            </h2>
            {error && (
              <p className="text-zinc-400 mb-6">{error}</p>
            )}

            {/* Plan card */}
            {pendingPlan && (
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 mb-6 text-left">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-white font-semibold">{planName} Plan</p>
                    <p className="text-xs text-zinc-500 capitalize">{billingCycle} billing</p>
                  </div>
                  <CreditCard className="w-5 h-5 text-indigo-400" />
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
                  <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" />14-day free trial</span>
                  <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" />No charge today</span>
                  <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" />Cancel anytime</span>
                </div>
              </div>
            )}

            <div className="flex gap-3 mb-4">
              <Button
                onClick={handleManualCheckout}
                className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white h-11"
                data-testid="retry-checkout-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
              <Button
                onClick={() => { window.history.pushState({}, '', '/contact'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5 h-11"
                data-testid="contact-support-btn"
              >
                Contact Support
              </Button>
            </div>

            <button
              onClick={() => { logout(); navigate('/'); }}
              className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
              data-testid="billing-setup-signout"
            >
              Sign out
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
